import { useCallback, useEffect, useRef, useState } from "react";
import { useTranslation } from "react-i18next";
import * as Sentry from "@sentry/react";
import {
  clearSnooze,
  computeMaxBumpCandidates,
  isSnoozeState,
  partitionSnoozedRegressions,
  snoozeExercise,
  type CurrentMax,
  type Exercise,
  type MaxBumpDirection,
  type PendingMaxBumps,
  type RegressionSnoozeState,
} from "@jackedlogs/shared";
import { useMaxBumpCandidates, useMaxesData, type MaxBumpInput } from "@jackedlogs/ui";

import { apiFetch, ApiError } from "../api";
import { durableWrite } from "../durableWrite";
import { notifyViaWindow } from "../confirm";
import { lsCache } from "../lsCache";
import { fetchAndPersistExercises } from "../logger/exercisesCache";

const SNOOZE_CACHE = lsCache<RegressionSnoozeState>("maxes/regressionSnooze.v1");

// web-rnw twin of apps/mobile/src/screens/useMaxBumpFlow.ts (RW-maxbump-web).
// Same pure decision core (computeMaxBumpCandidates + the shared
// maxRegressionSnooze rules, both @jackedlogs/shared) and the same shared
// candidate-state container (useMaxBumpCandidates, @jackedlogs/ui) — only
// the platform mechanics differ, same split as useLoggerHostDeps.ts:
//   - no offline outbox: durableWrite (sent/rejected only, the seam every
//     other web write already goes through) replaces writeOrQueue+maxOutbox.
//   - lsCache (localStorage) replaces AsyncStorage for the snooze state.
//   - notifyViaWindow replaces Alert.alert.
//   - @sentry/react (same captureMessage API as mobile's react-native SDK)
//     replaces the native import.
//   - currentMaxes/maxMeta come from useMaxesData directly (web has no
//     MaxesContext singleton — every consumer calls the hook itself, same
//     as MaxesScreen.tsx / useWorkoutStarter.ts).
export function useMaxBumpFlowWeb(): {
  pendingMaxBumps: PendingMaxBumps | null;
  catalog: Exercise[] | null;
  maxBumpBusy: ReadonlySet<string>;
  evaluateAfterSave: (maxBumpInput?: MaxBumpInput) => void;
  onConfirmMaxBump: (
    exerciseId: string,
    suggestedKg: number,
    direction?: MaxBumpDirection,
  ) => void;
  onDismissMaxBump: (exerciseId: string) => void;
  onSnoozeMaxBump: (exerciseId: string) => void;
} {
  const { t } = useTranslation();
  const {
    maxes: currentMaxes,
    maxMeta,
    loaded: maxesLoaded,
    forceRefresh: refreshMaxes,
  } = useMaxesData({ apiFetch });
  const {
    pending: pendingMaxBumps,
    setPending: setMaxBumpPending,
    acceptCandidate: acceptMaxBumpCandidate,
  } = useMaxBumpCandidates();
  const [maxBumpBusy, setMaxBumpBusy] = useState<Set<string>>(new Set());
  const [catalog, setCatalog] = useState<Exercise[] | null>(null);

  // Best-effort catalog hydration for the banner's exercise-name lookup —
  // same fetchAndPersistExercises() the logger's post-save summary already
  // uses (apps/web-rnw/src/logger/PostSessionSummaryCard.tsx).
  useEffect(() => {
    let cancelled = false;
    void fetchAndPersistExercises()
      .then((exs) => {
        if (!cancelled) setCatalog(exs);
      })
      .catch(() => {
        // Best-effort: the banner falls back to the slug.
      });
    return () => {
      cancelled = true;
    };
  }, []);

  // Persisted "bad day" snooze state — localStorage twin of mobile's
  // AsyncStorage read, held in a ref (read/written imperatively in
  // callbacks) and restored once on mount.
  const snoozeRef = useRef<RegressionSnoozeState>({});
  useEffect(() => {
    let cancelled = false;
    void SNOOZE_CACHE.read().then((raw) => {
      if (cancelled || !raw) return;
      if (isSnoozeState(raw)) snoozeRef.current = raw;
    });
    return () => {
      cancelled = true;
    };
  }, []);
  const persistSnooze = useCallback((next: RegressionSnoozeState) => {
    snoozeRef.current = next;
    void SNOOZE_CACHE.write(next);
  }, []);

  const evaluateAfterSave = useCallback(
    (maxBumpInput?: MaxBumpInput) => {
      // Skip when maxes haven't been fetched yet — every weighted set would
      // otherwise project as "new MAX" against an empty map.
      if (!maxBumpInput || !maxesLoaded) return;
      const currentMaxesObj: Record<string, number | null | undefined> = {};
      for (const [slug, kg] of currentMaxes.entries()) currentMaxesObj[slug] = kg;
      const currentMaxesDetailed: Record<string, CurrentMax | null | undefined> = {};
      for (const [slug, m] of maxMeta.entries()) currentMaxesDetailed[slug] = m;
      const pending = computeMaxBumpCandidates({
        workout: maxBumpInput.workout,
        exercisePatterns: maxBumpInput.exercisePatterns,
        currentMaxes: currentMaxesObj,
        currentMaxesDetailed,
        recentBestE1RM: maxBumpInput.recentBestE1RM,
        workoutId: maxBumpInput.workoutId,
      });
      // Apply the "bad day" snooze to the downward (lower) candidates, same
      // rule mobile applies.
      const lowerIds = (pending?.candidates ?? [])
        .filter((c) => c.direction === "lower")
        .map((c) => c.exerciseId);
      let next = pending;
      if (lowerIds.length > 0) {
        const { suppress, next: nextSnooze } = partitionSnoozedRegressions(
          lowerIds,
          snoozeRef.current,
        );
        if (nextSnooze !== snoozeRef.current) persistSnooze(nextSnooze);
        if (pending && suppress.size > 0) {
          const kept = pending.candidates.filter(
            (c) => !(c.direction === "lower" && suppress.has(c.exerciseId)),
          );
          next = kept.length > 0 ? { ...pending, candidates: kept } : null;
        }
      }
      setMaxBumpPending(next);
    },
    [maxesLoaded, currentMaxes, maxMeta, setMaxBumpPending, persistSnooze],
  );

  const onConfirmMaxBump = useCallback(
    (exerciseId: string, suggestedKg: number, direction?: MaxBumpDirection) => {
      if (maxBumpBusy.has(exerciseId)) return;
      setMaxBumpBusy((prev) => {
        const next = new Set(prev);
        next.add(exerciseId);
        return next;
      });
      // Same source-tagging rule as mobile: a raise is a conservative
      // derived estimate, a lower comes from a genuine ceiling/fail attempt.
      const source = direction === "lower" ? "tested" : "derived";
      void durableWrite({
        method: "PUT",
        path: `/api/me/maxes/${encodeURIComponent(exerciseId)}`,
        body: { valueKg: suggestedKg, source },
      })
        .then(async (result) => {
          if (result.outcome === "rejected") {
            Sentry.captureMessage("maxes.confirm_rejected", {
              level: "error",
              extra: {
                status: result.error instanceof ApiError ? result.error.status : null,
              },
            });
            notifyViaWindow(
              t("mobile.workouts.maxConfirmFailedTitle"),
              t("mobile.workouts.maxConfirmFailedBody"),
            );
            return;
          }
          persistSnooze(clearSnooze(snoozeRef.current, exerciseId));
          await refreshMaxes();
          acceptMaxBumpCandidate(exerciseId);
        })
        .finally(() => {
          setMaxBumpBusy((prev) => {
            const next = new Set(prev);
            next.delete(exerciseId);
            return next;
          });
        });
    },
    [maxBumpBusy, refreshMaxes, acceptMaxBumpCandidate, persistSnooze, t],
  );

  const onDismissMaxBump = useCallback(
    (exerciseId: string) => {
      persistSnooze(clearSnooze(snoozeRef.current, exerciseId));
      acceptMaxBumpCandidate(exerciseId);
    },
    [acceptMaxBumpCandidate, persistSnooze],
  );

  const onSnoozeMaxBump = useCallback(
    (exerciseId: string) => {
      persistSnooze(snoozeExercise(snoozeRef.current, exerciseId));
      acceptMaxBumpCandidate(exerciseId);
    },
    [acceptMaxBumpCandidate, persistSnooze],
  );

  return {
    pendingMaxBumps,
    catalog,
    maxBumpBusy,
    evaluateAfterSave,
    onConfirmMaxBump,
    onDismissMaxBump,
    onSnoozeMaxBump,
  };
}
